import React, { useMemo, useRef } from 'react';
import clsx from 'clsx';
import useCheckoutState from '../../hooks/useCheckoutState';
import useCheckoutFormHelpers from '../../hooks/useCheckoutFormHelpers';
import StyledElement from '../StyledElement';
import { InlineSuccessCheckmark } from '../SuccessCheckmark';
import Button from '../Button';
import FormField from '../CheckoutForm/components/FormField';
import ErrorMessage from '../CheckoutForm/components/ErrorMessage';
import { DEFAULT_GUESTS_SAVE_BUTTON, guestSectionText, resolveFormLayout } from '@repo/lib';
import {
  FormDivider,
  FormHeading,
  FormLayoutItems,
} from '../CheckoutForm/components/FormLayout/FormLayoutFields';
import { GuestFieldsProvider } from '../../contexts/GuestFieldsContext';
import {
  bookerEmailOf,
  cancelledRow,
  checkoutGuestSettings,
  currentGuestRows,
  orderedTicketTypes,
  rowErrors,
  rowAsBooker,
  rowValue,
  savedGuest,
  savedRow,
  withRowValue,
} from '../../lib/guests/checkoutGuests';

const ROW = 'cp_guest-row border-b border-[var(--cp_checkout-divider-border-color)] py-4';
const SECONDARY = 'text-[var(--cp_text-secondary)]';
const ROW_TEXT =
  '[font-size:var(--cp_font-body-size)] [line-height:var(--cp_font-body-line-height)] [font-weight:var(--cp_font-body-weight)]';
const TICK = 'size-4 shrink-0 text-[var(--cp_checkout-confirmation-mark-color)]';
const TITLE = clsx(
  'cp_guest-title min-w-0 truncate [color:var(--cp_checkout-form-fields-label-color)] [font-family:var(--cp_checkout-form-fields-label-font-family)] [font-weight:var(--cp_checkout-form-fields-label-font-weight)] [font-size:var(--cp_checkout-form-fields-label-font-size)]',
);

const idOf = value => String(value?._id ?? value ?? '');

/** The seller's heading and description above the guest rows; with neither, the rows sit flush. */
export function GuestsIntro({ settings, children, ...rowsProps }) {
  const { heading, description } = guestSectionText(settings);
  return (
    <>
      {heading ? <FormHeading item={{ text: heading }} /> : null}
      {description ? (
        <div className={clsx('cp_guests-description mb-3', SECONDARY, ROW_TEXT)}>{description}</div>
      ) : null}
      <div
        className={clsx(
          'cp_guest-rows',
          (heading || description) && 'border-t border-[var(--cp_checkout-divider-border-color)]',
        )}
        {...rowsProps}
      >
        {children}
      </div>
    </>
  );
}

/** No guest opens by itself: the booker opens one with Add, and Save attendee opens the next. */
export function openRowIndex(rows, panel) {
  if (panel?.open !== undefined) return panel.open < rows.length ? panel.open : -1;
  return -1;
}

export function nextOpenRow(rows, from) {
  const after = rows.findIndex((row, at) => at > from && !row.done);
  return after !== -1 ? after : rows.findIndex((row, at) => at !== from && !row.done);
}

export function errorFor(errors, question) {
  if (!errors) return null;
  if (question.type === 'name') return errors.name;
  if (question.type === 'email') return errors.email;
  return errors.answers?.[idOf(question._id)];
}

export function withoutError(errors, question) {
  if (!errors) return errors;
  if (question.type === 'name') return { ...errors, name: undefined };
  if (question.type === 'email') return { ...errors, email: undefined };
  const answers = { ...errors.answers };
  delete answers[idOf(question._id)];
  return { ...errors, answers };
}

export function AllAttendeesAdded() {
  return (
    <div
      className={clsx(
        'cp_guests-complete flex items-center gap-2 pt-3 text-[var(--cp_text-primary)]',
        ROW_TEXT,
      )}
    >
      <InlineSuccessCheckmark className={clsx('cp_guests-complete-icon', TICK)} />
      All attendees added
    </div>
  );
}

export function GuestTitle({ index, typeName, required = false }) {
  const attendee = `Attendee ${index + 1}`;
  return (
    <div className={TITLE} title={`${attendee} · ${typeName}`}>
      <span className={clsx(required && 'after:content-["*"] after:ml-0.5')}>{attendee}</span>{' '}
      <span className={clsx('[font-weight:var(--cp_font-body-weight)]', SECONDARY)}>
        · {typeName}
      </span>
    </div>
  );
}

function RowLink({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className={clsx('cp_guest-row-link underline shrink-0', ROW_TEXT, {
        'opacity-40 cursor-default': disabled,
      })}
      disabled={disabled}
      onClick={event => {
        event.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

// Groups flatten into their items, in order, where side-by-side columns don't fit.
const withoutGroups = items => items.flatMap(item => (item.type === 'group' ? item.items : [item]));

// rowErrors puts its own message under a blank required field or a bad email. The browser's bubble
// would focus the field, and Chrome's autofill list then covers the bubble.
const browserOnlyProblem = field => {
  const { validity } = field;
  if (validity.valid || validity.valueMissing) return false;
  return !(validity.typeMismatch && field.type === 'email');
};

/** Reports the first limit only the browser checks (number, date, length); false when it showed one. */
export function reportInvalidField(container) {
  const invalid = [...(container?.querySelectorAll('input, select, textarea') ?? [])].find(
    browserOnlyProblem,
  );
  invalid?.reportValidity();
  return !invalid;
}

/** One attendee's questions, laid out with their headings and drawn with the checkout's fields. */
export function GuestQuestionFields({
  row,
  index,
  questions,
  layout = null,
  errors,
  onChange,
  groups = true,
  errorText = error => error,
}) {
  const values = useMemo(
    () =>
      Object.fromEntries(
        questions.map(question => [
          idOf(question._id),
          { ...question, _id: idOf(question._id), value: rowValue(row, question) },
        ]),
      ),
    [questions, row],
  );
  const guestFields = useMemo(() => ({ values, questions: Object.values(values) }), [values]);
  const items = useMemo(() => {
    const resolved = resolveFormLayout(layout, questions);
    return groups ? resolved : withoutGroups(resolved);
  }, [layout, questions, groups]);

  const renderQuestion = question => {
    const questionId = idOf(question._id);
    const error = errorText(errorFor(errors, question));
    return (
      <div key={questionId}>
        <FormField
          field={{
            ...question,
            _id: `${questionId}-guest-${index}`,
            questionId,
            autoCompleteSection: `guest-${index}`,
            ...(question.element === 'address' && { title: question.label }),
            placeholder:
              question.placeholder ||
              (['text', 'email'].includes(question.element) ? question.label : undefined),
            value: rowValue(row, question) ?? '',
          }}
          onChange={value => onChange(question, value)}
        />
        {error && <ErrorMessage className="cp_error -mt-2 mb-4">{error}</ErrorMessage>}
      </div>
    );
  };

  return (
    <GuestFieldsProvider value={guestFields}>
      <FormLayoutItems items={items} renderField={renderQuestion} />
    </GuestFieldsProvider>
  );
}

export function OpenRow({
  row,
  index,
  typeName,
  questions,
  layout = null,
  errors,
  onChange,
  onSave,
  onCancel,
  onThisIsMe,
  pending = false,
  required = false,
  saveLabel,
}) {
  const rowRef = useRef(null);
  const save = () => {
    if (reportInvalidField(rowRef.current)) onSave();
  };

  return (
    <div
      ref={rowRef}
      className={clsx(ROW, 'outline-none')}
      data-guest-row={index}
      data-open="true"
      tabIndex={-1}
    >
      <div className="flex justify-between items-center gap-3">
        <GuestTitle index={index} typeName={typeName} required={required} />
        {onThisIsMe && <RowLink onClick={onThisIsMe}>Use booking details</RowLink>}
      </div>
      <div className="pt-4">
        <GuestQuestionFields
          row={row}
          index={index}
          questions={questions}
          layout={layout}
          errors={errors}
          onChange={onChange}
        />
        {pending && (
          <ErrorMessage className="cp_error cp_guest-pending-error mb-4">
            Save or cancel attendee {index + 1} first.
          </ErrorMessage>
        )}
        <div className="flex items-center justify-end gap-4">
          {onCancel && <RowLink onClick={onCancel}>Cancel</RowLink>}
          <StyledElement
            elementPath="checkout.form.button"
            as={Button}
            className="cp_button cp_save-guest-button whitespace-nowrap leading-snug px-6"
            type="button"
            appearance="submit"
            onClick={save}
          >
            {saveLabel || DEFAULT_GUESTS_SAVE_BUTTON}
          </StyledElement>
        </div>
      </div>
    </div>
  );
}

export function ClosedRow({ row, index, typeName, onEdit, locked = false, required = false }) {
  const done = row.done ?? row.added;
  const shown = row.done ? savedGuest(row) : row;
  const summary = [shown.name, shown.email].filter(Boolean).join(', ');
  return (
    <div
      className={clsx(ROW, { 'cursor-pointer': !locked })}
      data-guest-row={index}
      role={locked ? undefined : 'button'}
      tabIndex={locked ? undefined : 0}
      onClick={locked ? undefined : onEdit}
      onKeyDown={event => {
        if (locked) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit();
        }
      }}
    >
      <div
        className={clsx('flex justify-between gap-3', row.done ? 'items-start' : 'items-center')}
      >
        {row.done ? (
          <div className="flex items-center gap-2 min-w-0">
            <InlineSuccessCheckmark className={clsx('cp_guest-done-icon', TICK)} />
            <div className="min-w-0">
              <GuestTitle index={index} typeName={typeName} required={required} />
              <div className={clsx('truncate', SECONDARY, ROW_TEXT)} title={summary}>
                {summary}
              </div>
            </div>
          </div>
        ) : (
          <GuestTitle index={index} typeName={typeName} required={required} />
        )}
        <RowLink onClick={onEdit} disabled={locked}>
          {done ? 'Edit' : 'Add'}
        </RowLink>
      </div>
    </div>
  );
}

export default function GuestRows() {
  const { checkout, form } = useCheckoutState();
  const { setFormStateByKey } = useCheckoutFormHelpers();
  const settings = checkoutGuestSettings(checkout);
  const questions = checkout.guestQuestions ?? [];
  const { saveButton: saveLabel } = guestSectionText(checkout.eventDetails?.attendeeDetails);

  const rows = currentGuestRows(checkout, form);
  if (!settings || rows.length === 0) return null;

  const panel = form.guestPanel ?? {};
  const open = openRowIndex(rows, panel);
  const typeNames = Object.fromEntries(
    orderedTicketTypes(checkout).map(type => [idOf(type._id), type.name]),
  );

  const setPanel = next => setFormStateByKey('guestPanel', next);
  const setRows = next => setFormStateByKey('guests', next);

  const changeValue = (index, question, value) => {
    setRows(rows.map((row, at) => (at === index ? withRowValue(row, question, value) : row)));
    const errors = panel.errors?.[index];
    if (errorFor(errors, question)) {
      setPanel({ ...panel, errors: { ...panel.errors, [index]: withoutError(errors, question) } });
    }
  };

  const save = index => {
    const errors = rowErrors({
      rows,
      index,
      questions,
      bookerEmail: bookerEmailOf(form.fields),
      emailDomains: settings.emailDomains,
    });
    if (errors.name || errors.email || Object.keys(errors.answers ?? {}).length) {
      setPanel({ ...panel, open: index, errors: { ...panel.errors, [index]: errors } });
      return;
    }
    const next = rows.map((r, at) => (at === index ? savedRow(r, questions) : r));
    setRows(next);
    setPanel({ open: nextOpenRow(next, index), errors: { ...panel.errors, [index]: undefined } });
  };

  // Guest 1 is often the booker, so one click copies their name and email in.
  const fillWithBooker = () => {
    const filled = rowAsBooker(rows[0], questions, form.fields);
    setRows(rows.map((row, at) => (at === 0 ? filled : row)));
    const errors = panel.errors?.[0];
    if (errors) {
      setPanel({
        ...panel,
        errors: {
          ...panel.errors,
          0: {
            ...questions
              .filter(q => q.type === 'first-name' || q.type === 'last-name')
              .reduce((left, question) => withoutError(left, question), errors),
            name: undefined,
            email: undefined,
          },
        },
      });
    }
  };

  const edit = index => setPanel({ ...panel, open: index, pending: undefined });

  const cancel = index => {
    setRows(rows.map((row, at) => (at === index ? cancelledRow(row) : row)));
    setPanel({
      ...panel,
      open: -1,
      pending: undefined,
      errors: { ...panel.errors, [index]: undefined },
    });
  };

  return (
    <div className="cp_guests mb-6" data-guest-rows="">
      <FormDivider />
      <GuestsIntro settings={checkout.eventDetails?.attendeeDetails}>
        {rows.map((row, index) =>
          index === open ? (
            <OpenRow
              key={index}
              row={row}
              index={index}
              typeName={typeNames[idOf(row.ticketType)]}
              questions={questions}
              layout={checkout.attendeeFormLayout}
              errors={panel.errors?.[index]}
              onChange={(question, value) => changeValue(index, question, value)}
              onSave={() => save(index)}
              onCancel={() => cancel(index)}
              onThisIsMe={index === 0 ? fillWithBooker : undefined}
              pending={panel.pending === index}
              required={settings.requireAllBeforePayment}
              saveLabel={saveLabel}
            />
          ) : (
            <ClosedRow
              key={index}
              row={row}
              index={index}
              typeName={typeNames[idOf(row.ticketType)]}
              onEdit={() => edit(index)}
              required={settings.requireAllBeforePayment}
            />
          ),
        )}
      </GuestsIntro>
      {open === -1 && rows.every(row => row.done) && <AllAttendeesAdded />}
    </div>
  );
}
